/**
 * QR rendering utilities used by the bulk-download flow.
 *
 * renderQRToPNG produces a PNG Blob at a deterministic size (1024×1024 bare,
 * or taller when labeled). buildRoomFilename produces the per-room filename
 * inside the resulting ZIP.
 */
import { createElement } from 'react'
import { renderToString } from 'react-dom/server'
import { QRCodeSVG } from 'qrcode.react'

// Rendering constants. Kept together so the labeled/bare geometry is easy
// to audit against the spec ("1024×1024 bare; labeled adds a fixed label
// area below").
const QR_SIZE = 1024
const LABEL_AREA_HEIGHT = 200
const ROOM_FONT_PX = 56
const BUILDING_FONT_PX = 36
const LABEL_TOP_PADDING = 48
const LINE_SPACING = 16

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Ensures an SVG string declares the SVG XML namespace on its root element.
 *
 * React 19's `renderToString` + qrcode.react v4 emits `<svg …>` without the
 * `xmlns` attribute. Chromium's HTMLImageElement decoder rejects xmlns-less
 * SVGs and fires `onerror`, which breaks the bulk-download flow (the SVG
 * blob is loaded via `<img src>` so it can be rasterised onto a canvas).
 *
 * This helper is idempotent — if the root already declares `xmlns=`, the
 * input is returned unchanged. Exported for unit testing.
 */
export function ensureSvgNamespace(svg: string): string {
  return svg.includes('xmlns=') ? svg : svg.replace('<svg', `<svg xmlns="${SVG_NS}"`)
}

export interface RenderQRToPNGOpts {
  /** When true, the PNG includes room + building labels below the QR. */
  labeled: boolean
  roomName: string
  buildingName: string
}

export interface CanvasDimensions {
  width: number
  height: number
  qrSize: number
  labelAreaHeight: number
}

/**
 * Slugifies a name for use in a filename segment.
 * Rules: lowercase, spaces→dashes, strip any char outside [a-z0-9-],
 * collapse consecutive dashes, trim leading/trailing dashes, truncate to
 * 60 chars. This is the canonical slug rule used everywhere (ZIP names,
 * room filenames, building filenames).
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

/**
 * Computes canvas dimensions for a rendered QR PNG.
 * Pure function — useful for unit tests without a DOM.
 */
export function computeCanvasDimensions(labeled: boolean): CanvasDimensions {
  const labelAreaHeight = labeled ? LABEL_AREA_HEIGHT : 0
  return {
    width: QR_SIZE,
    height: QR_SIZE + labelAreaHeight,
    qrSize: QR_SIZE,
    labelAreaHeight,
  }
}

/**
 * Builds the per-room filename inside a ZIP.
 * Format: `{building-slug}_{room-slug}.png` for the first occurrence.
 * Appends `-2`, `-3`, … before `.png` for collisions (caller supplies
 * the collision counter — tracking happens in bulkQRZip).
 */
export function buildRoomFilename(
  room: { name: string },
  building: { name: string },
  collisionCount: number
): string {
  const buildingSlug = slugify(building.name)
  const roomSlug = slugify(room.name)
  const base = `${buildingSlug}_${roomSlug}`
  if (collisionCount === 0) {
    return `${base}.png`
  }
  return `${base}-${collisionCount + 1}.png`
}

/**
 * Renders a QR code for `url` to a PNG Blob.
 * Uses qrcode.react's SVG renderer, rasterises to a canvas, optionally
 * draws a label block beneath.
 */
export async function renderQRToPNG(
  url: string,
  opts: RenderQRToPNGOpts
): Promise<Blob> {
  const dims = computeCanvasDimensions(opts.labeled)

  const rawSvgString = renderToString(
    createElement(QRCodeSVG, {
      value: url,
      size: dims.qrSize,
      level: 'M',
      includeMargin: false,
    })
  )
  const svgString = ensureSvgNamespace(rawSvgString)
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const svgUrl = URL.createObjectURL(svgBlob)

  try {
    const img = await loadImage(svgUrl)
    const canvas = document.createElement('canvas')
    canvas.width = dims.width
    canvas.height = dims.height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Unable to acquire 2D canvas context for QR rendering')
    }

    // Solid white background — matches the print/download output in the
    // single-room flow (see RoomQRCode.handleDownload) for visual parity.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, dims.width, dims.height)
    ctx.drawImage(img, 0, 0, dims.qrSize, dims.qrSize)

    if (opts.labeled) {
      drawLabels(ctx, dims, opts.roomName, opts.buildingName)
    }

    return await canvasToBlob(canvas)
  } finally {
    URL.revokeObjectURL(svgUrl)
  }
}

function drawLabels(
  ctx: CanvasRenderingContext2D,
  dims: CanvasDimensions,
  roomName: string,
  buildingName: string
): void {
  const centerX = dims.width / 2
  const labelTop = dims.qrSize + LABEL_TOP_PADDING

  ctx.fillStyle = '#000000'
  ctx.font = `bold ${ROOM_FONT_PX}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText(roomName, centerX, labelTop)

  ctx.font = `${BUILDING_FONT_PX}px sans-serif`
  ctx.fillStyle = '#666666'
  ctx.fillText(buildingName, centerX, labelTop + ROOM_FONT_PX + LINE_SPACING)
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load QR SVG into image'))
    img.src = src
  })
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas.toBlob returned null'))
    }, 'image/png')
  })
}
